import {
  ErrorResponse,
  ExposedError,
  IPaymentIntent,
} from '@stripe/terminal-js'
import { TFunction } from '@transifex/react'
import { LSPAYErrorMessage, SwiperError } from './lspay.types'
import {
  CANCELATION_MESSAGES,
  CARD_SPECIFIC_DECLINE_REASON,
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  STRIPE_CARD_DIGITS_ERRORS,
  STRIPE_DECLINE_CODE,
  STRIPE_ERROR_CODE,
  SWIPER_CANCEL_REASON,
  SWIPER_DECLINE_REASON,
  SWIPER_ERROR_CODES,
} from './lspayConstants'

/** Verify that the object passed in is of type ErrorResponse */
export const isErrorResponse = (response: any): response is ErrorResponse =>
  !!(response as ErrorResponse).error

/** Verify that the object passed in is of type ExposedError */
export const isExposedError = (response: any): response is ExposedError =>
  !!(response as ExposedError).message

/** Verify that the object passed in contains a payment intent */
export const isErrorWithIntent = (
  error: any
): error is { payment_intent: IPaymentIntent } => !!error.payment_intent

/** This error is here to separate "terminal disconnected" errors from others */
export class TerminalDisconnectedError extends Error {}

export const onUnexpectedDisconnect = () => {
  // FIXME this won't do anything by itself. We may reset the terminal state instead
  throw new TerminalDisconnectedError('Reader disconnected unexpectedly')
}

/** Returns true when the error is due to a cancellation */
export const isCancellation = (error?: any) =>
  error?.code === STRIPE_ERROR_CODE.CANCELLED ||
  CANCELATION_MESSAGES.includes(error?.message)

// Map decline codes to the proper LSPAYErrorMessage. If not found, we return undefined and the caller should use a generic message.
const declineCodeToErrorMessage: Record<
  STRIPE_DECLINE_CODE,
  LSPAYErrorMessage
> = {
  insufficient_funds:
    LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CARD_DECLINED_INSUFFICIENT],
  transaction_not_allowed:
    LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED],
  incorrect_pin: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN],
  invalid_account:
    LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT],
  invalid_amount:
    LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CARD_DECLINED_INVALID_AMOUNT],
  pin_try_exceeded:
    LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED],
}

// Map swiper decline reasons to the proper LSPAY_ERROR.
const swiperDeclineReasonToError: Record<SWIPER_DECLINE_REASON, LSPAY_ERROR> = {
  TERMINAL_UNAVAILABLE: LSPAY_ERROR.TERMINAL_DISCONNECTED,
  UNKNOWN: LSPAY_ERROR.DEFAULT_PAYMENT,
  CARD_DECLINED: LSPAY_ERROR.CARD_DECLINED,
  EXPIRED_CARD: LSPAY_ERROR.CARD_EXPIRED,
  INSUFFICIENT_FUNDS: LSPAY_ERROR.CARD_DECLINED_INSUFFICIENT,
  INVALID_AMOUNT: LSPAY_ERROR.CARD_DECLINED_INVALID_AMOUNT,
  INVALID_ACCOUNT: LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT,
  TRANSACTION_NOT_ALLOWED: LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED,
  INCORRECT_PIN: LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN,
  PIN_TRY_EXCEEDED: LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED,
}

const M = LSPAY_ERROR_MESSAGES
const ERR = LSPAY_ERROR
const CODE = STRIPE_ERROR_CODE

type SWIPER_REFUND_DECLINE_REASON = Exclude<
  SWIPER_DECLINE_REASON,
  | SWIPER_DECLINE_REASON.INSUFFICIENT_FUNDS
  | SWIPER_DECLINE_REASON.INVALID_AMOUNT
>
// Map refund decline reasons to the proper LSPAYErrorMessage.

const swiperRefundDeclineReasonToError: Record<
  SWIPER_REFUND_DECLINE_REASON,
  LSPAY_ERROR
> = {
  UNKNOWN: LSPAY_ERROR.DEFAULT_REFUND,
  TERMINAL_UNAVAILABLE: LSPAY_ERROR.TERMINAL_DISCONNECTED,
  CARD_DECLINED: LSPAY_ERROR.CARD_DECLINED_REFUND,
  EXPIRED_CARD: LSPAY_ERROR.CARD_EXPIRED_REFUND,
  INVALID_ACCOUNT: LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT_REFUND,
  TRANSACTION_NOT_ALLOWED: LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED_REFUND,
  INCORRECT_PIN: LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN_REFUND,
  PIN_TRY_EXCEEDED: LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED_REFUND,
}

/**
 * Return the proper payment error message based on the error received.
 * Note the error given is likely a Stripe `ExposedError` but could in some cases be something else
 *
 * See available codes on simulated cards: https://stripe.com/docs/terminal/testing#test-cards-for-specific-error-cases
 * and suggestions on handling them: https://stripe.com/docs/declines/codes
 **/
export const getPaymentErrorMessage = (error?: any) => {
  // User cancellations
  if (isCancellation(error)) {
    return M[ERR.CANCEL_PAYMENT]
  }

  // We lost the connection to the terminal
  if (error instanceof TerminalDisconnectedError) {
    return M[ERR.TERMINAL_DISCONNECTED]
  }

  // Card expired
  if (error?.code === CODE.EXPIRED) {
    return M[ERR.CARD_EXPIRED]
  }

  // Card declined
  if (error?.code === CODE.DECLINED) {
    return (
      declineCodeToErrorMessage[error.decline_code as STRIPE_DECLINE_CODE] ??
      M[ERR.CARD_DECLINED]
    )
  }

  // Those are very odd in a card-present transaction. Reuse the card declined generic message.
  if (STRIPE_CARD_DIGITS_ERRORS.includes(error?.code)) {
    return M[ERR.CARD_DECLINED]
  }

  // Reader issues
  if (error?.code === CODE.READER_ERROR) {
    switch (error.message) {
      case 'Cannot start transaction with card inserted from previous transaction, remove card and try again.':
        return M[ERR.REMOVE_PREVIOUS_CARD]
      case 'Cannot collect payment. The device may be busy performing updates or changing settings.':
      case 'Cannot collect payment while Reader application is in the background. The device may be busy performing updates or changing settings.':
        return M[ERR.READER_BACKGROUND]
    }
  }

  // Connectivity issue on browser
  if (error?.code === CODE.NETWORK_TIMEOUT) {
    return M[ERR.BROWSER_CONNECTIVITY]
  }

  // Connectivity issue on terminal
  if (error?.code === CODE.CONNECTION_ERROR) {
    return M[ERR.TERMINAL_CONNECTIVITY]
  }

  return M[ERR.DEFAULT_PAYMENT]
}

/**
 * Return the proper refund declined error message based on the reason received.
 *  See decline reasons here https://lightspeedhq.atlassian.net/wiki/spaces/LRXPAY/pages/1441795306/LSD+Card-present+refunds+v2
 **/

function isRefundDeclinedReason(
  reason: string
): reason is SWIPER_REFUND_DECLINE_REASON {
  return Object.values(SWIPER_DECLINE_REASON).includes(
    reason as SWIPER_REFUND_DECLINE_REASON
  )
}

export const getRefundDeclinedError = (reason: string) => {
  if (isRefundDeclinedReason(reason)) {
    return swiperRefundDeclineReasonToError[reason] ?? ERR.DEFAULT_REFUND
  }
  return ERR.DEFAULT_REFUND
}

function isSwiperDeclinedReason(
  reason: string
): reason is SWIPER_DECLINE_REASON {
  return Object.values(SWIPER_DECLINE_REASON).includes(
    reason as SWIPER_DECLINE_REASON
  )
}

export const isCardSpecificDecline = (reason: string): boolean => {
  if (isSwiperDeclinedReason(reason)) {
    return Object.values(CARD_SPECIFIC_DECLINE_REASON).includes(
      (reason as unknown) as CARD_SPECIFIC_DECLINE_REASON
    )
  }
  return false
}

function isSwiperCancelledReason(
  reason: string
): reason is SWIPER_CANCEL_REASON {
  return Object.values(SWIPER_CANCEL_REASON).includes(
    reason as SWIPER_CANCEL_REASON
  )
}

/**
 * Return the proper payment declined error based on the reason received.
 *  See decline codes on https://stripe.com/docs/declines/codes
 **/

export const getPaymentDeclinedError = (reason: string): LSPAY_ERROR => {
  if (isSwiperDeclinedReason(reason)) {
    return swiperDeclineReasonToError[reason] ?? ERR.DEFAULT_PAYMENT
  }
  return ERR.DEFAULT_PAYMENT
}

export const getCancelledError = (
  reason: string,
  isRefund: boolean = false
) => {
  if (isSwiperCancelledReason(reason)) {
    if (
      [
        SWIPER_CANCEL_REASON.INTENT_CANCELLED,
        SWIPER_CANCEL_REASON.TERMINAL_TIMEOUT,
        SWIPER_CANCEL_REASON.TERMINAL_UNAVAILABLE,
      ].includes(reason)
    ) {
      return isRefund
        ? ERR.CANCEL_REFUND_WITH_INTENT_CANCELLED
        : ERR.CANCEL_PAYMENT_WITH_INTENT_CANCELLED
    }

    if (reason === SWIPER_CANCEL_REASON.TERMINAL_BUSY) {
      return isRefund ? ERR.DEFAULT_REFUND : ERR.DEFAULT_PAYMENT
    }
  }
  return isRefund ? ERR.CANCEL_REFUND : ERR.CANCEL_PAYMENT
}

const getAmountTooSmallErrorMessage = (
  amountTooSmallError: SwiperError,
  vdCurrencyFilter: (value: any) => string,
  retailerPaymentTypeName: string,
  t: TFunction
): LSPAYErrorMessage => {
  const minimumAmount = amountTooSmallError.fields?.minimum_amount
  const body = t(
    'You can choose another payment type or adjust the amount to pay and retry this payment.',
    {
      _context: 'Payment error body',
    }
  )
  const header = minimumAmount
    ? t(
        `This payment can't be taken because {retailerPaymentTypeName} has a minimum payment amount of {minAmountFormatted}.`,
        {
          _context: 'Payment error header',
          retailerPaymentTypeName,
          minAmountFormatted: vdCurrencyFilter(
            parseInt(minimumAmount, 10) / 100
          ),
        }
      )
    : t(
        `This payment can't be taken because the specified amount is too small.`,
        { _context: 'Payment error header' }
      )
  return {
    header,
    body,
    action: t('Got it, thanks', { _context: 'Payment error action' }),
  }
}

const getCNPNotFoundErrorMessage = (
  retailerPaymentTypeName: string,
  t: TFunction
): LSPAYErrorMessage => {
  return {
    header: t(
      `This payment can't be taken with {retailerPaymentTypeName} because this outlet is not registered to use this payment type`,
      {
        _context: 'Payment error header',
        retailerPaymentTypeName,
      }
    ),
    body: t('Please choose another payment type to complete this sale.', {
      _context: 'Payment error body',
    }),
    action: t('Got it, thanks', { _context: 'Payment error action' }),
  }
}

export const getLspayErrorMessage = ({
  err,
  vdCurrencyFilter,
  retailerPaymentTypeName,
  t,
  getErrorMessage,
}: {
  err: SwiperError
  vdCurrencyFilter: (value: any) => string
  retailerPaymentTypeName: string
  t: TFunction
  getErrorMessage: (error: LSPAY_ERROR) => LSPAYErrorMessage
}): LSPAYErrorMessage => {
  switch (err.code) {
    case SWIPER_ERROR_CODES.LSPAY_AMOUNT_TOO_SMALL:
      return getAmountTooSmallErrorMessage(
        err,
        vdCurrencyFilter,
        retailerPaymentTypeName,
        t
      )
    case SWIPER_ERROR_CODES.LSPAY_GET_CARD_NOT_PRESENT_CONFIG_NOT_FOUND:
      return getCNPNotFoundErrorMessage(retailerPaymentTypeName, t)
    case SWIPER_ERROR_CODES.LSPAY_PMOF_PAYMENT_DECLINED: {
      const declinedReason =
        err.fields?.reason === SWIPER_DECLINE_REASON.UNKNOWN
          ? SWIPER_DECLINE_REASON.CARD_DECLINED
          : err.fields?.reason
      return getErrorMessage(getPaymentDeclinedError(declinedReason))
    }
    default:
      return getErrorMessage(LSPAY_ERROR.DEFAULT_PAYMENT)
  }
}

const SWIPER_ERROR_CODES_NON_RETRYABLE = [
  SWIPER_ERROR_CODES.CODE_REGISTER_PAIRING_NOT_FOUND,
  SWIPER_ERROR_CODES.LSPAY_AMOUNT_TOO_SMALL,
  // Unref refund errors
  SWIPER_ERROR_CODES.LSPAY_UNREF_REFUND_EXCEED_LIMIT,
  SWIPER_ERROR_CODES.LSPAY_UNREF_REFUND_SETTINGS_NOT_FOUND,
  SWIPER_ERROR_CODES.LSPAY_UNREF_REFUND_NOT_ENABLED,
]
export const isRetryableError = (errorCode: number) => {
  if (SWIPER_ERROR_CODES_NON_RETRYABLE.includes(errorCode)) {
    return false
  }

  return true
}
